import { useState, useEffect, useContext } from 'react';
import { Button, Row, Col } from 'antd';
import { history } from 'umi';
import { useSelector, useDispatch } from 'dva';
import { Form, Typography, message, Checkbox } from 'iglooform';
import { useIntl } from 'react-intl';
import { HeaderTitle } from '@/components/header-title/header-title';
import throttle from 'lodash/throttle';
import { login, getAgencyInfo } from './service';
import { Rule } from 'rc-field-form/lib/interface';
import { checkPhoneNumber } from '@/utils/regexp';
import { updateRequest } from '@/service/request';
import Context from '@/utils/context';
import {
  EnterKeyPressListener,
  GA_KEY,
  aesDecryptLoginPassWord,
  aesEncryptLoginPassWord,
  desEncryptLoginPassword,
} from '@/utils/common';

import './style/login.less';

const LoginPage = () => {
  const form = Form.useForm();
  const { isPc } = useContext(Context);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { currentLang } = useSelector<ReduxState, GlobalState>(
    (state) => state.global,
  );

  useEffect(() => {
    EnterKeyPressListener(handleLogin);
    return () => {
      document.body.removeEventListener('keydown', handleLogin);
    };
  }, []);

  const isRememberMeDefaultValue = localStorage.getItem('rm') === 'true';

  const [isRememberMe, setIsRememberMe] = useState<boolean>(
    isRememberMeDefaultValue,
  );

  const checkRememberMeCallBack = () => {
    if (isRememberMe) {
      localStorage.setItem('rm', String(isRememberMe));
      localStorage.setItem(
        'rm-pn',
        form.getFieldValue(['login', 'phone_number', 'phoneNumber']),
      );
      localStorage.setItem(
        'rm-pw',
        aesEncryptLoginPassWord(form.getFieldValue(['login', 'password'])),
      );
    } else {
      localStorage.removeItem('rm');
      localStorage.removeItem('rm-pn');
      localStorage.removeItem('rm-pw');
    }
  };

  const onChangeRM = (e: any) => {
    setIsRememberMe(e?.target?.checked);
  };

  const handleLogin = throttle(
    async (values: any): Promise<void> => {
      try {
        const params = values.login;
        login({
          phone_area_code: '84',
          phone_number: params.phone_number?.phoneNumber.trim(),
          password: desEncryptLoginPassword(params.password?.trim()),
        }).then((res) => {
          if (res && res.user_token) {
            checkRememberMeCallBack();
            handleResponse(res);
          }
        });
      } catch (error) {
        console.error('log in error: ', error);
        window.gtag('event', 'login', { success: false });
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  // 处理返回结果
  const handleResponse = (res: { user_token: string }) => {
    localStorage.setItem(TOKEN_NAME, res.user_token);
    updateRequest(
      localStorage.getItem(SELECTED_LANG) === 'vi-VN' ? 'vi-VN' : 'en-US',
    );
    getAgencyInfo().then((info: { userInfo: UserInfo }) => {
      if (info && info.userInfo) {
        const { payment_password_exist } = info.userInfo;
        dispatch({
          type: 'global/setData',
          payload: {
            token: res.user_token,
            currentLang,
          },
        });
        if (payment_password_exist) {
          dispatch({
            type: 'global/setData',
            payload: {
              menuKey: 'home',
            },
          });
          history.push('/home');
          message.success(formatMessage({ id: 'Log in successfully' }));
        } else {
          // 该用户还未设置支付密码
          history.replace('/login/set-payment-password');
        }

        // 登录时埋点
        window.gtag('event', 'login', {
          success: true,
          user_unique_id: info.userInfo.id,
        });

        // 全页面埋点
        window.gtag('config', ENV !== 'dev' ? GA_KEY.PROD : GA_KEY.DEV, {
          user_unique_id: info.userInfo.id,
        });
      }
    });
  };

  useEffect(() => {
    // 先判断是否remember me，再判断是否有刚注册跳过来
    if (isRememberMeDefaultValue) {
      form.setFields([
        {
          name: ['login', 'phone_number', 'phoneNumber'],
          value: localStorage.getItem('rm-pn'),
        },
        {
          name: ['login', 'password'],
          value: aesDecryptLoginPassWord(
            localStorage.getItem('rm-pw') as string,
          ),
        },
      ]);
    } else {
      const onboardingPhoneNumber = localStorage.getItem('PHONENUMBER');
      onboardingPhoneNumber &&
        form.setFields([
          {
            name: ['login', 'phone_number', 'phoneNumber'],
            value: onboardingPhoneNumber,
          },
        ]);
    }

    return localStorage.removeItem('PHONENUMBER');
  }, []);

  return (
    <Row style={{ marginTop: '80px' }}>
      <Col
        span={isPc ? 12 : 24}
        style={{ maxWidth: '538px', margin: '0 auto' }}
      >
        <HeaderTitle
          title={formatMessage({ id: 'Welcome!' })}
          subTitle={formatMessage({ id: 'Log in to continue.' })}
        />
        <div className="form-container">
          <Form
            form={form}
            config={{
              type: 'Login',
              name: 'login',
              elements: [
                {
                  type: 'PhoneNumber',
                  label: formatMessage({ id: 'Mobile Number' }),
                  name: 'phone_number',
                  areaCode: '+84',
                  minLength: 8,
                  fullRow: true,
                  required: true,
                  role: { validator: checkPhoneNumber },
                },
                {
                  type: 'Password',
                  label: formatMessage({ id: 'Password' }),
                  name: 'password',
                  fullRow: true,
                  extraLabel: (
                    <Button
                      type="link"
                      onClick={() => history.push('/login/forget-password')}
                    >
                      {formatMessage({ id: 'Forgot Password' })}
                    </Button>
                  ),
                  rule: {
                    validator: (_: Rule, value: string) => {
                      if (value && value.length < 8) {
                        return Promise.reject(
                          formatMessage({
                            id: 'Password must be at least 8 characters',
                          }),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                },
                {
                  render: () => {
                    return (
                      <Checkbox
                        className="checkbox-rm"
                        checked={isRememberMe}
                        onChange={onChangeRM}
                      >
                        {formatMessage({ id: 'Remember me' })}
                      </Checkbox>
                    );
                  },
                },
              ],
            }}
            onSubmit={(values) => handleLogin(values)}
            locales={{
              submitText: formatMessage({
                id: 'Login',
                defaultMessage: 'Login',
              }),
              validateMessages: {
                required:
                  '${label} ' +
                  formatMessage({
                    id: 'is required',
                  }),
              },
            }}
          />
          <section style={{ marginTop: '-16px' }}>
            <Typography
              level="body2"
              style={{
                display: 'block',
                marginBottom: '8px',
                color: '#999',
                fontFamily: 'Roboto',
              }}
            >
              {formatMessage({ id: 'By click Login, you agree to our' })}
              <a
                style={{ marginLeft: '4px', fontStyle: 'oblique' }}
                href="https://contents.iglooinsure.com/privacypolicy"
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage({ id: 'Privacy and Policy &' })}
              </a>
              <a
                style={{ marginLeft: '4px', fontStyle: 'oblique' }}
                href="https://contents.iglooinsure.com/termsofservice"
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage({ id: 'Terms of Service' })}
              </a>
            </Typography>
            <Typography
              level="body2"
              style={{ color: '#999', fontFamily: 'Roboto' }}
            >
              {formatMessage({ id: 'Don’t have an account?' })}
              <a
                style={{ marginLeft: '4px', fontStyle: 'oblique' }}
                onClick={() => history.push('/onboarding')}
                target="_blank"
                rel="noreferrer"
              >
                {formatMessage({ id: 'Register Now' })}
              </a>
            </Typography>
          </section>
        </div>
      </Col>
    </Row>
  );
};

export default LoginPage;
